/* eslint-disable import/prefer-default-export */
import { captureException } from '@sentry/nextjs';
import { incrementMetric } from '../Services/Datadog';
import {
  encryptWithPublicKey,
  getMaxEncryptableByteLength,
  importPublicKey,
} from './encryption';

/**
 * isTooLongToEncrypt
 *
 * RSA can only encrypt a value shorter than its key. A null maxByteLength means
 * the key did not tell us its limit, so we let the encryption be attempted.
 */
const isTooLongToEncrypt = (text, maxByteLength) => {
  if (maxByteLength === null) return false;

  return new TextEncoder().encode(text).length > maxByteLength;
};

/**
 * getEncryptedAnswers
 *
 * collects together data pertaining to the answers given by a customer. In order to protect any PII, these values are encrypted.
 * Answers that cannot be encrypted are left out rather than failing the batch, so that the answers which did encrypt are still reported.
 * Each answer left out is counted in Datadog under a reason tag, as the partner never receives it.
 */
export const getEncryptedAnswers = async (
  submissionAnswers,
  components,
  publicKeyString,
  formId
) => {
  const publicKey = await importPublicKey(publicKeyString);
  const maxByteLength = getMaxEncryptableByteLength(publicKey);
  const answersWithValues = Object.values(submissionAnswers).filter(
    (answer) =>
      typeof answer.value === 'object' && answer.value.some((value) => value)
  );

  // Format and encrypt values of answers object. Using this Promise/Entry pattern to accommodate for the asynchronous logic in the loop callback.
  const answersArray = await Promise.all(
    answersWithValues.map(async (answer) => {
      const componentId = answer.id;
      const answeredComponent = components[`component_${componentId}`];
      if (answeredComponent && answeredComponent?.component?.mappedQuestionId) {
        const answerText = answer.value.join(', ');
        const { mappedQuestionId } = answeredComponent.component;

        if (isTooLongToEncrypt(answerText, maxByteLength)) {
          incrementMetric({
            name: 'answerTooLongToEncrypt',
            tags: {
              reason: 'too_long',
              mapped_question_id: mappedQuestionId,
              form_id: formId,
            },
          });

          return undefined;
        }

        try {
          const value = await encryptWithPublicKey(answerText, publicKey);
          return [componentId, { value, mappedQuestionId }];
        } catch (error) {
          // isTooLongToEncrypt already filters the expected oversized-answer case,
          // so anything reaching here is unexpected and worth reporting.
          captureException(error);
          incrementMetric({
            name: 'answerTooLongToEncrypt',
            tags: {
              reason: 'unexpected_error',
              mapped_question_id: mappedQuestionId,
              form_id: formId,
            },
          });

          return undefined;
        }
      }

      return undefined;
    })
  );
  return Object.fromEntries(answersArray.filter(Boolean));
};
